import firebase from "firebase/app";
import "firebase/storage";
import "firebase/firestore";
import React, { useState } from "react";
import { firestore, storage } from "../firebase/firebase";
import SheetMusic from "../interfaces/sheetmusic";
import Button from "./Button";
import Input from "./Input";

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  editMode?: boolean;
  editSheet?: SheetMusic;
}

const UploadModal = ({ isOpen, onClose, editMode = false, editSheet }: UploadModalProps) => {
  const [author, setauthor] = useState("");
  const [title, settitle] = useState("");
  const [files, setfiles] = useState<File[]>([]);
  const [error, seterror] = useState<Error | null>(null);
  const [success, setsuccess] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles) {
      let fileList: File[] = [];
      for (let i = 0; i < selectedFiles.length; i++) {
        fileList.push(selectedFiles[i]);
      }
      setfiles(fileList);
    }
  };

  const isValid = author !== "" && title !== "" && (editMode || files.length > 0);

  const resetForm = () => {
    setauthor("");
    settitle("");
    setfiles([]);
    seterror(null);
    setsuccess(null);
    setUploadProgress(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Initialize form with edit data when in edit mode
  React.useEffect(() => {
    if (editMode && editSheet && isOpen) {
      setauthor(editSheet.author);
      settitle(editSheet.title);
    } else if (!editMode && isOpen) {
      resetForm();
    }
  }, [editMode, editSheet, isOpen]);

  const formSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    seterror(null);
    setsuccess(null);

    // Handle edit mode
    if (editMode && editSheet) {
      const musicRef = firestore.collection("music").doc(editSheet.id);
      
      musicRef.update({
        author: author,
        title: title,
      })
      .then(() => {
        setsuccess("Notis veiksmīgi rediģētas!");
        setTimeout(() => {
          handleClose();
        }, 2000);
      })
      .catch((error) => seterror(error));
      
      return;
    }

    // Handle upload mode
    const storageRef = storage.ref();

    files.forEach((file) => {
      let metadata = {
        contentType: file.type,
      };
      let fileRef = storageRef.child(`music/${file.name}`);

      try {
        var uploadTask = fileRef.put(file, metadata);

        uploadTask.on(
          firebase.storage.TaskEvent.STATE_CHANGED,
          (snapshot) => {
            let progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
            console.log("Upload is " + progress + "% done");
            switch (snapshot.state) {
              case firebase.storage.TaskState.PAUSED:
                console.log("Upload is paused");
                break;
              case firebase.storage.TaskState.RUNNING:
                console.log("Upload is running");
                break;
              default:
                setUploadProgress(null);
                console.log("default snapshot state", snapshot.state);
                break;
            }
          },
          (e) => {
            setUploadProgress(null);

            const error = e as firebase.FirebaseError;
            if (!error) {
              console.error(e);
              return;
            }

            switch (error.code) {
              case "storage/unauthorized":
                console.error(
                  "User doesn't have permission to access the object"
                );
                break;
              case "storage/canceled":
                console.error("Upload cancelled by user");
                break;
              case "storage/unknown":
                console.error("Unknown error");
                break;
              default:
                console.log("Unknown error occurred");
                break;
            }
          },
          () => {
            uploadTask.snapshot.ref.getDownloadURL().then((url) => {
              console.log(`File available at: ${url}`);

              let musicRef = firestore.collection("music").doc();

              let music: SheetMusic = {
                id: musicRef.id,
                downloadURL: url,
                author: author,
                name: file.name,
                title: title,
                size: file.size,
                added_on: firebase.firestore.Timestamp.fromDate(new Date()),
              };

              musicRef
                .set(music)
                .then(() => {
                  setsuccess("Fails veiksmīgi augšuplādēts!");
                  setUploadProgress(100);
                  setTimeout(() => {
                    handleClose();
                  }, 2000);
                })
                .catch((error) => seterror(error));
            });
          }
        );
      } catch (e) {
        console.error(e);
        return;
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-zinc-800 ">
              {editMode ? "Rediģēt notis" : "Pievienot notis"}
            </h2>
            <button
              onClick={handleClose}
              className="text-zinc-500 hover:text-zinc-700 text-2xl leading-none"
            >
              ×
            </button>
          </div>

          <form onSubmit={formSubmit} className="space-y-6">
            {!editMode && (
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2 ">
                  Izvēlēties failu
                </label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFiles}
                  className="w-full p-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-zinc-500 focus:border-zinc-500 "
                />
              </div>
            )}
            <Input
              label="Dziesmas nosaukums"
              type="text"
              value={title}
              onChange={settitle}
              placeholder="Dziesmas nosaukums"
            />

            <Input
              label="Dziesmas autors"
              type="text"
              value={author}
              onChange={setauthor}
              placeholder="Dziesmas autors"
            />

            {error && (
              <div className="p-3 bg-red-100 border border-red-300 rounded-lg">
                <p className="text-red-700 text-sm ">{error.message}</p>
              </div>
            )}

            {success && (
              <div className="p-3 bg-green-100 border border-green-300 rounded-lg">
                <p className="text-green-700 text-sm ">{success}</p>
              </div>
            )}

            {uploadProgress !== null && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-zinc-600 ">
                  <span>Augšuplādē...</span>
                  <span>{Math.round(uploadProgress)}%</span>
                </div>
                <div className="w-full bg-zinc-200 rounded-full h-2">
                  <div
                    className="bg-zinc-700 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={!isValid || uploadProgress !== null}
                className="flex-1"
              >
                {uploadProgress !== null ? "Augšuplādē..." : (editMode ? "Saglabāt izmaiņas" : "Pievienot")}
              </Button>
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 border border-zinc-300 text-zinc-700 rounded-lg hover:bg-zinc-50  font-semibold transition-colors"
              >
                Atcelt
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;